/**
 * ResolutionOptionsModal
 * 
 * Modal component that displays resolution options when scope expansion
 * is detected after adding jobs to a ticket. Allows user to select
 * and apply a resolution strategy.
 */

import { promisePlanRows, type IssuePlansLite } from '@/lib/optimizer/normalizeOptimizerResponse';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  AlertTriangle,
  Clock,
  Users,
  ArrowRight,
  Zap,
  CheckCircle2,
  Loader2,
  Info,
} from 'lucide-react';
import type { ResolutionOption, BoardIssue } from '@/hooks/useProvisionalOptimizer';
import { cn } from '@/lib/utils';

interface ResolutionOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: BoardIssue | null;
  options: ResolutionOption[];
  selectedOption: ResolutionOption | null;
  onSelectOption: (option: ResolutionOption) => void;
  onApply: () => Promise<boolean>;
  onSkip: () => void;
  isLoading: boolean;
  /** Core-100 CORE-BH/BG: every issue the change triggers (only promise issues are shown). */
  promiseIssues?: BoardIssue[];
  /** Core-100 CORE-BH/BG: the corrective planners' suggestion-only menus, by issue id. */
  issuePlans?: Record<string, IssuePlansLite>;
}

export function ResolutionOptionsModal({
  open,
  onOpenChange,
  issue,
  options,
  selectedOption,
  onSelectOption,
  onApply,
  onSkip,
  isLoading,
  promiseIssues = [],
  issuePlans = {},
}: ResolutionOptionsModalProps) {
  // Core-100 R4 W5: one pure reading of the promise rows (runtime-proven against the served optimizer).
  const promises = promisePlanRows(promiseIssues, issuePlans);
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    setApplying(true);
    const success = await onApply();
    setApplying(false);
    if (success) {
      onOpenChange(false);
    }
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) {
      return <Badge variant="default" className="bg-green-500/20 text-green-700 dark:text-green-400">High Confidence</Badge>;
    }
    if (confidence >= 0.5) {
      return <Badge variant="secondary">Medium Confidence</Badge>;
    }
    return <Badge variant="outline">Low Confidence</Badge>;
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'absorb':
        return <Zap className="h-4 w-4 text-green-500" />;
      case 'reschedule':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'reassign':
        return <Users className="h-4 w-4 text-purple-500" />;
      case 'defer':
        return <ArrowRight className="h-4 w-4 text-orange-500" />;
      case 'notify':
        return <Info className="h-4 w-4 text-muted-foreground" />;
      default:
        return <CheckCircle2 className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Schedule Impact Detected
          </DialogTitle>
          <DialogDescription>
            Adding these jobs will extend the ticket duration. Choose how to handle this change.
          </DialogDescription>
        </DialogHeader>

        {issue && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {issue.message}
            </p>
            {issue.context && (
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex gap-4">
                {(issue.context as any).addedMinutes && (
                  <span>+{(issue.context as any).addedMinutes} minutes added</span>
                )}
                {(issue.context as any).newOptiTime && (
                  <span>New duration: {(issue.context as any).newOptiTime} min</span>
                )}
              </div>
            )}
          </div>
        )}

        {promises.length > 0 && (
          <div className="rounded-lg border p-3 mb-4 space-y-2" data-testid="promise-corrective-plans">
            <p className="text-sm font-medium">Promises affected</p>
            {promises.map((p) => (
              <div key={p.issueId} className="text-sm space-y-1" data-testid="promise-plan-row">
                <p>{p.message}</p>
                {p.actions.length > 0 && (
                  <ul className="list-disc pl-5 text-xs text-muted-foreground">
                    {p.actions.map((label) => <li key={label}>{label}</li>)}
                  </ul>
                )}
                {p.waiterNote && <p className="text-xs text-muted-foreground">{p.waiterNote}</p>}
              </div>
            ))}
            <p className="text-xs text-muted-foreground">These are suggestions for a person to act on — OptiBay does not apply them.</p>
          </div>
        )}

        <ScrollArea className="max-h-[400px] pr-4">
          <RadioGroup
            value={selectedOption?.id}
            onValueChange={(id) => {
              const option = options.find(o => o.id === id);
              if (option) onSelectOption(option);
            }}
            className="space-y-3"
          >
            {options.map((option, index) => (
              <Card
                key={option.id}
                className={cn(
                  'cursor-pointer transition-all',
                  selectedOption?.id === option.id
                    ? 'ring-2 ring-primary border-primary'
                    : 'hover:border-muted-foreground/50'
                )}
                onClick={() => onSelectOption(option)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={option.id} id={option.id} />
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Label htmlFor={option.id} className="cursor-pointer font-semibold">
                            {option.label}
                          </Label>
                          {option.autoApplyEligible && (
                            <Badge variant="outline" className="text-xs">
                              Auto-eligible
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {option.description}
                        </CardDescription>
                      </div>
                    </div>
                    {getConfidenceBadge(option.confidence)}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <Separator className="mb-3" />
                  
                  {/* Impact Summary */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    {option.impactSummary.ticketsAffected > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {option.impactSummary.ticketsAffected} ticket{option.impactSummary.ticketsAffected !== 1 ? 's' : ''} affected
                      </span>
                    )}
                    {option.impactSummary.minutesShifted > 0 && (
                      <span className="flex items-center gap-1">
                        <ArrowRight className="h-3 w-3" />
                        {option.impactSummary.minutesShifted} min shifted
                      </span>
                    )}
                    {option.impactSummary.techsInvolved.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {option.impactSummary.techsInvolved.length} tech{option.impactSummary.techsInvolved.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Actions Preview */}
                  <div className="flex flex-wrap gap-2">
                    {option.actions.map((action, actionIndex) => (
                      <Badge
                        key={actionIndex}
                        variant="secondary"
                        className="flex items-center gap-1 text-xs"
                      >
                        {getActionIcon(action.type)}
                        {action.type}
                      </Badge>
                    ))}
                  </div>

                  {/* Score indicator */}
                  <div className="mt-3 flex items-center gap-2">
                    <div className="text-xs text-muted-foreground">Score:</div>
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.min(100, option.score)}%` }}
                      />
                    </div>
                    <div className="text-xs font-medium">{option.score.toFixed(0)}</div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {options.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No resolution options available</p>
                <p className="text-sm">The changes can be applied without adjustments</p>
              </div>
            )}
          </RadioGroup>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onSkip}
            disabled={applying}
          >
            Skip (No Changes)
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedOption || applying}
          >
            {applying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Apply Resolution
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
